import React, { useRef } from 'react';

const EXPERTISE_AREAS = [
  {
    title: "Generative AI",
    body: "We build production chatbots and assistants using retrieval-augmented generation, tool orchestration, prompt evaluation, and rigorous guardrails so outputs stay reliable."
  },
  {
    title: "Agentic AI",
    body: "Ilmium Agent ships with planning, tool use, memory, telemetry, and human-in-the-loop controls so complex workflows can run safely with clear audit trails."
  },
  {
    title: "Computer Vision",
    body: "We deliver loss prevention and store intelligence systems covering detection, tracking, re-identification, pose estimation, action recognition, and privacy-first deployment."
  }
];

const TiltCard = ({ children, className = "", as: Tag = "article", tabIndex = 0, ...rest }) => {
  const cardRef = useRef(null);

  const updateTilt = (x, y, rect) => {
    if (!cardRef.current) return;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;

    cardRef.current.style.setProperty('--tilt-y', `${rotateX.toFixed(2)}deg`);
    cardRef.current.style.setProperty('--tilt-x', `${rotateY.toFixed(2)}deg`);
    cardRef.current.style.setProperty('--glow-x', `${(x / rect.width) * 100}%`);
    cardRef.current.style.setProperty('--glow-y', `${(y / rect.height) * 100}%`);
    cardRef.current.style.setProperty('--glow-opacity', '1');
  };

  const handleMouseMove = (event) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    updateTilt(x, y, rect);
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.setProperty('--tilt-y', '0deg');
    cardRef.current.style.setProperty('--tilt-x', '0deg');
    cardRef.current.style.setProperty('--glow-x', '50%');
    cardRef.current.style.setProperty('--glow-y', '50%');
    cardRef.current.style.setProperty('--glow-opacity', '0');
  };

  const handleFocus = () => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    updateTilt(rect.width / 2, rect.height / 2, rect);
  };

  const handleBlur = () => {
    handleMouseLeave();
  };

  return (
    <Tag
      className={className}
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={tabIndex}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const ExpertiseCard = ({ title, body, className = "" }) => {
  return (
    <TiltCard className={`expertise-card ${className}`}>
      <h3>{title}</h3>
      <p>{body}</p>
    </TiltCard>
  );
};

const Expertise = () => {
  return (
    <section id="expertise" className="container expertise">
      <TiltCard
        as="header"
        className="section-heading expertise-heading expertise-card expertise-intro"
      >
        <h2 className="section-title expertise-title">Expertise</h2>
        <p className="section-sub expertise-sub">
          <span className="expertise-highlight">
            <span className="expertise-highlight-text">
              We design, build, and operate AI systems that create measurable value.
            </span>
          </span>
        </p>
      </TiltCard>

      <div className="expertise-grid">
        {EXPERTISE_AREAS.map((item) => (
          <ExpertiseCard
            key={item.title}
            title={item.title}
            body={item.body}
            className={item.title === "Agentic AI" ? "agentic" : "ex-mobile-hide"}
          />
        ))}
      </div>
    </section>
  );
};

export default Expertise;
